import { Call } from '../Call';
import { reconcileRingState } from '../ringing';
import { Dispatcher } from '../rtc';
import {
  handleRemoteSoftMute,
  watchAudioLevelChanged,
  watchCallEnded,
  watchCallGrantsUpdated,
  watchConnectionQualityChanged,
  watchDominantSpeakerChanged,
  watchInboundStateNotification,
  watchLiveEnded,
  watchParticipantCountChanged,
  watchParticipantJoined,
  watchParticipantLeft,
  watchParticipantUpdated,
  watchPinsUpdated,
  watchSfuCallEnded,
  watchSfuErrorReports,
  watchTrackPublished,
  watchTrackUnpublished,
} from '../events';

/**
 * Registers the default event handlers for a call during its lifecycle.
 *
 * @param call the call to register event handlers for.
 * @param dispatcher the dispatcher.
 */
export const registerEventHandlers = (call: Call, dispatcher: Dispatcher) => {
  const state = call.state;
  // Read lazily on each event: setE2EEManager can run after setup() (an app that
  // inspects call settings via get()/getOrCreate() before deciding to encrypt has
  // already triggered it), so a value captured here would be a stale undefined
  // for the whole call and orphaned tracks would never get a decryptor.
  const e2ee = () => call.e2eeManager;

  const eventHandlers = [
    call.on('call.ended', watchCallEnded(call)),
    watchSfuCallEnded(call),

    watchLiveEnded(dispatcher, call),
    watchSfuErrorReports(dispatcher),
    watchConnectionQualityChanged(dispatcher, state),
    watchParticipantCountChanged(dispatcher, state),

    call.on('participantJoined', watchParticipantJoined(state, e2ee)),
    call.on('participantLeft', watchParticipantLeft(state)),
    call.on('participantUpdated', watchParticipantUpdated(state)),

    call.on('trackPublished', watchTrackPublished(state, e2ee)),
    call.on('trackUnpublished', watchTrackUnpublished(state, e2ee)),

    watchAudioLevelChanged(dispatcher, state),
    watchDominantSpeakerChanged(dispatcher, state),

    call.on('callGrantsUpdated', watchCallGrantsUpdated(state)),
    call.on('pinsUpdated', watchPinsUpdated(state)),
    call.on('inboundStateNotification', watchInboundStateNotification(state)),

    handleRemoteSoftMute(call),
  ];

  return () => {
    eventHandlers.forEach((unsubscribe) => unsubscribe());
  };
};

/**
 * Registers event handlers for a call that is of ringing type.
 *
 * @param call the call to register event handlers for.
 */
export const registerRingingCallEventHandlers = (call: Call) => {
  const reconcile = () => {
    reconcileRingState(call, 'ring-ws').catch((err) => {
      call.logger.error('Failed to reconcile the ring state', err);
    });
  };

  // each event needs its own closure. `call.missed` is deliberately absent:
  // nothing in the reconciler acts on `missed_by`, so the auto-drop owns the
  // "nobody answered" case until the server-owned ring timeout lands.
  const eventHandlers = [
    call.on('call.accepted', () => reconcile()),
    call.on('call.rejected', () => reconcile()),
  ];

  return () => {
    eventHandlers.forEach((unsubscribe) => unsubscribe());
  };
};
